import FiberNewIcon from '@mui/icons-material/FiberNew';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CopyToClipboardButton } from '../../../../components/CopyToClipboardButton';
import { ProtectedContent } from '../../../../components/ProtectedContent';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import { useAppDispatch } from '../../../../redux/hooks';
import { createAlpacaAccount, fetchAccountByUserId } from '../../../../redux/modules/alpaca/alpaca.actions';
import { alpacaSelector } from '../../../../redux/modules/alpaca/alpaca.selectors';
import * as permissions from '../../../../utils/permissions';
import { AlpacaEventsTable } from './AlpacaEventsTable';
import { AlpacaDividendsTable } from './AlpacaDividendsTable';

import { Field, FieldGrid, SectionCard } from '../../../../components/Section';

interface AlpacaTabProps {
  id: number;
}

export function AlpacaTab({ id }: AlpacaTabProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { account, error, createAlpacaAccountStatus } = useSelector(alpacaSelector);

  useEffect(() => {
    if (id) {
      dispatch(fetchAccountByUserId(id));
    }
  }, []);

  const createAccount = async () => {
    if (id) {
      try {
        await dispatch(createAlpacaAccount(id)).unwrap();

        enqueueSnackbar('Alpaca Account created successfully!', { variant: 'success' });
      } catch (err: any) {
        enqueueSnackbar('Create Alpaca Account was failed', { variant: 'error' });
      }
      dispatch(fetchAccountByUserId(id));
    }
  };

  const renderAccountInfo = () => {
    if (!account) {
      return (
        <Grid item>
          <ProtectedContent items={[permissions.AlpacaPermissions.createAccount]}>
            <LoadingButton
              id="CreateAccountButton"
              color="primary"
              variant="contained"
              sx={{ ml: 2 }}
              onClick={createAccount}
              startIcon={<FiberNewIcon />}
              loading={createAlpacaAccountStatus === fetchStatuses.pending}
              loadingPosition="end"
            >
              Create Account
            </LoadingButton>
          </ProtectedContent>
        </Grid>
      );
    }

    return (
      <>
        <SectionCard title="Alpaca account">
          <FieldGrid>
            <Field label="Account id">
              <CopyToClipboardButton text={account.id} value={account.id} />
            </Field>
            <Field label="Status" value={account.status} />
          </FieldGrid>
        </SectionCard>
        <SectionCard title="Events" disablePadding sx={{ mt: '16px', '& .MuiDataGrid-root': { border: 0 } }}>
          <AlpacaEventsTable accountId={account.id} />
        </SectionCard>
        <SectionCard title="Dividends" disablePadding sx={{ mt: '16px', '& .MuiDataGrid-root': { border: 0 } }}>
          <AlpacaDividendsTable userId={id} />
        </SectionCard>
      </>
    );
  };

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      {renderAccountInfo()}
    </>
  );
}
